import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { PerfectOrderFileResponse } from 'src/types/perfectOrder';
import { ACCEPTED_FILE_UPLOAD_TYPES, MAX_FILE_UPLOAD_COUNT, validateFileUpload } from 'src/utils/fileUploadRules';

interface Props {
  files: PerfectOrderFileResponse[];
  onUploadFiles?: (files: File[]) => Promise<void>;
  onDeleteFile?: (file: PerfectOrderFileResponse) => Promise<void>;
  uploading?: boolean;
  deletingFileId?: number | null;
  disabled?: boolean;
}

type PreviewKind = 'image' | 'video';

const PerfectOrderFilesGrid: FC<Props> = ({ files, onUploadFiles, onDeleteFile, uploading = false, deletingFileId = null, disabled = false }) => {
  const [selectedPreview, setSelectedPreview] = useState<{ url: string; kind: PreviewKind } | null>(null);
  const [fileToDelete, setFileToDelete] = useState<PerfectOrderFileResponse | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const sortedFiles = useMemo(() => {
    return [...(files || [])].sort((a, b) => (a.perfectOrderFileId ?? 0) - (b.perfectOrderFileId ?? 0));
  }, [files]);

  const isImageFile = (file: PerfectOrderFileResponse, fileName: string): boolean => {
    if (file.contentType?.toLowerCase().startsWith('image/')) {
      return true;
    }

    const valueToCheck = fileName || file.fileUrl;
    if (!valueToCheck) {
      return false;
    }

    return /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif)(\?.*)?$/i.test(valueToCheck);
  };

  const isVideoFile = (file: PerfectOrderFileResponse, fileName: string): boolean => {
    if (file.contentType?.toLowerCase().startsWith('video/')) {
      return true;
    }

    const valueToCheck = fileName || file.fileUrl;
    if (!valueToCheck) {
      return false;
    }

    return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(valueToCheck);
  };

  const getFileTypeLabel = (file: PerfectOrderFileResponse, fileName: string): string => {
    const extension = fileName.includes('.') ? fileName.split('.').pop()?.trim() : undefined;
    if (extension) {
      return extension.slice(0, 8).toUpperCase();
    }

    const contentSubtype = file.contentType?.split('/').pop()?.split(';')[0]?.trim();
    if (contentSubtype) {
      return contentSubtype.slice(0, 8).toUpperCase();
    }

    return 'FILE';
  };

  const getFileName = (file: PerfectOrderFileResponse): string => {
    if (file.fileName) {
      return file.fileName;
    }

    try {
      const url = new URL(file.fileUrl, window.location.origin);
      const pathName = url.pathname.split('/').filter(Boolean).pop();
      return pathName ? decodeURIComponent(pathName) : 'Attachment';
    } catch {
      const pathName = file.fileUrl.replace(/\\/g, '/').split('/').filter(Boolean).pop();
      return pathName ? decodeURIComponent(pathName.split('?')[0]) : 'Attachment';
    }
  };

  const handleFileInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (disabled || selectedFiles.length === 0 || !onUploadFiles) {
      return;
    }

    const validation = validateFileUpload(selectedFiles, sortedFiles.length);

    if (!validation.isValid) {
      setUploadError(validation.error || 'Unable to attach files.');
      return;
    }

    setUploadError(null);
    await onUploadFiles(selectedFiles);
  };

  const handleConfirmDelete = async () => {
    if (disabled || !fileToDelete || !onDeleteFile) {
      return;
    }

    await onDeleteFile(fileToDelete);
    setFileToDelete(null);
  };

  const renderUploadTile = () => {
    if (!onUploadFiles) {
      return null;
    }

    const uploadDisabled = disabled || uploading || sortedFiles.length >= MAX_FILE_UPLOAD_COUNT;

    return (
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              width: 118,
              p: 0,
              borderColor: uploadDisabled ? 'action.disabled' : '#ccc',
              overflow: 'hidden',
              backgroundColor: '#fff',
              opacity: uploadDisabled ? 0.7 : 1,
            }}
          >
            <ButtonBase
              disabled={uploadDisabled}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: '100%',
                height: 96,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: uploadDisabled ? 'action.disabled' : 'primary.main',
              }}
              aria-label="Attach files"
            >
              {uploading ? <CircularProgress size={34} /> : <AddIcon sx={{ fontSize: 44 }} />}
            </ButtonBase>
          </Box>
        </CardContent>
        <CardActions sx={{ p: 0 }}>
          <Typography
            sx={{
              borderTop: '1px solid',
              borderColor: uploadDisabled ? 'action.disabled' : '#ccc',
              fontSize: 13,
              px: 0.5,
              py: 0.8,
              textAlign: 'center',
              color: uploadDisabled ? 'text.disabled' : 'text.secondary',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            Attach files
          </Typography>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ px: 1, pb: 1 }}>
        <h4 className="mb-0">Attachments</h4>

        {onUploadFiles && <input ref={fileInputRef} type="file" multiple accept={ACCEPTED_FILE_UPLOAD_TYPES} hidden onChange={handleFileInputChange} />}
      </Box>

      {uploadError && (
        <Typography variant="caption" color="error" component="div" sx={{ px: 1, pb: 1 }}>
          {uploadError}
        </Typography>
      )}

      <Box sx={{ maxHeight: 340, overflowY: 'auto', p: 1 }}>
        {sortedFiles.length === 0 && !onUploadFiles && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No files found.
          </Typography>
        )}

        {(sortedFiles.length > 0 || onUploadFiles) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
            {renderUploadTile()}
            {sortedFiles.map((file) => {
              const fileName = getFileName(file);
              const showImagePreview = isImageFile(file, fileName);
              const showVideoPreview = isVideoFile(file, fileName);
              const showPreview = showImagePreview || showVideoPreview;
              const isDeleting = deletingFileId === file.perfectOrderFileId;
              const fileTypeLabel = getFileTypeLabel(file, fileName);

              return (
                <Box
                  key={file.perfectOrderFileId}
                  sx={{
                    width: 168,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    backgroundColor: 'background.default',
                    position: 'relative',
                    '& .attachment-actions': {
                      opacity: 1,
                    },
                    '@media (hover: hover) and (pointer: fine)': {
                      '& .attachment-actions': {
                        opacity: 0,
                        pointerEvents: 'none',
                      },
                      '&:hover .attachment-actions, &:focus-within .attachment-actions': {
                        opacity: 1,
                        pointerEvents: 'auto',
                      },
                    },
                  }}
                >
                  <Box
                    className="attachment-actions"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                      transition: 'opacity 120ms ease-in-out',
                      bgcolor: 'rgba(255, 255, 255, 0.86)',
                      borderRadius: 1,
                      boxShadow: 1,
                    }}
                  >
                    <Tooltip title="Open attachment">
                      <IconButton component="a" href={file.fileUrl} target="_blank" rel="noopener noreferrer" size="small" aria-label={`Open ${fileName}`} sx={{ p: 0.5 }}>
                        <OpenInNewIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    {onDeleteFile && (
                      <Tooltip title="Delete attachment">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={disabled || isDeleting}
                            onClick={() => setFileToDelete(file)}
                            aria-label={`Delete ${fileName}`}
                            sx={{ p: 0.5 }}
                          >
                            {isDeleting ? <CircularProgress size={14} /> : <DeleteForeverIcon fontSize="inherit" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>

                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      left: 6,
                      top: 72,
                      zIndex: 2,
                      bgcolor: 'rgba(17, 24, 39, 0.78)',
                      color: '#fff',
                      borderRadius: 0.75,
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                      px: 0.7,
                      py: 0.35,
                      letterSpacing: 0,
                    }}
                  >
                    {fileTypeLabel}
                  </Typography>

                  <Box
                    sx={{
                      width: '100%',
                      height: 96,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: showPreview ? 'zoom-in' : 'default',
                    }}
                    onClick={() => {
                      if (showPreview && file.fileUrl) {
                        setSelectedPreview({
                          url: file.fileUrl,
                          kind: showVideoPreview ? 'video' : 'image',
                        });
                      }
                    }}
                  >
                    {showImagePreview ? (
                      <Box component="img" src={file.fileUrl} alt={fileName} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : showVideoPreview ? (
                      <Box component="video" src={file.fileUrl} muted playsInline preload="metadata" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1, textAlign: 'center' }}>
                        No preview
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <Tooltip title={fileName}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          px: 0.75,
                          py: 0.5,
                        }}
                      >
                        {fileName}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog open={Boolean(selectedPreview)} onClose={() => setSelectedPreview(null)} maxWidth="lg">
        <IconButton onClick={() => setSelectedPreview(null)} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }} aria-label="Close file preview">
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 2 }}>
          {selectedPreview?.kind === 'image' && (
            <Box component="img" src={selectedPreview.url} alt="Selected preview" sx={{ maxWidth: '85vw', maxHeight: '85vh', display: 'block' }} />
          )}
          {selectedPreview?.kind === 'video' && (
            <Box component="video" src={selectedPreview.url} controls autoPlay playsInline sx={{ maxWidth: '85vw', maxHeight: '85vh', display: 'block' }} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(fileToDelete)} onClose={() => setFileToDelete(null)}>
        <DialogTitle>Delete Attachment?</DialogTitle>
        <DialogContent>
          <DialogContentText>Delete {fileToDelete ? getFileName(fileToDelete) : 'this attachment'} from this Perfect Order?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileToDelete(null)} disabled={Boolean(fileToDelete && deletingFileId === fileToDelete.perfectOrderFileId)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={Boolean(fileToDelete && deletingFileId === fileToDelete.perfectOrderFileId)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerfectOrderFilesGrid;
